import React from 'react';
import company from '../../data/company.json';
import { ENTITY } from '@/lib/entity';

/**
 * SiteJsonLd — page-specific JSON-LD blocks for the public marketing pages.
 *
 * Pattern follows Phase 62: one or two structured-data blocks per page, all
 * derived from already-public data (entity.js + the founder bios already
 * shown on /about). Page renders normally if any optional data is absent.
 *
 * Usage: <SiteJsonLd page="about" /> | <SiteJsonLd page="for-lenders" /> |
 *        <SiteJsonLd page="contact" />
 */

const ORIGIN = 'https://gp-cap.com';
const LOGO_URL = `${ORIGIN}/gp-capital-stacked-frame.svg`;

const ORG_NODE = {
  '@type': 'FinancialService',
  '@id': `${ORIGIN}#organization`,
  name: ENTITY.brand,
  description: company.description,
  legalName: ENTITY.legalName,
  url: ORIGIN,
  logo: LOGO_URL,
  email: ENTITY.contact.general,
  taxID: `ABN ${ENTITY.abn}`,
  identifier: { '@type': 'PropertyValue', propertyID: 'ACN', value: ENTITY.acn },
  sameAs: [ENTITY.social.linkedin, ENTITY.social.facebook].filter(Boolean),
  areaServed: [
    { '@type': 'Country', name: 'Australia' },
    { '@type': 'Place', name: 'Asia-Pacific' },
  ],
  founder: [
    {
      '@type': 'Person',
      name: 'Giorgio Primiceri',
      jobTitle: 'Founder & Managing Director',
      worksFor: { '@id': `${ORIGIN}#organization` },
      sameAs: ENTITY.social.giorgioLinkedIn ? [ENTITY.social.giorgioLinkedIn] : undefined,
    },
  ],
  contactPoint: [
    {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: ENTITY.contact.general,
      areaServed: ['AU', 'APAC'],
      availableLanguage: ['English'],
    },
    {
      '@type': 'ContactPoint',
      contactType: 'sales',
      email: ENTITY.contact.general,
      areaServed: ['AU', 'APAC'],
      availableLanguage: ['English'],
    },
  ],
};

// Strip undefined/empty fields recursively so we never emit `"sameAs": undefined`.
const compact = (v) => {
  if (Array.isArray(v)) {
    const arr = v.map(compact).filter((x) => x !== undefined && x !== null && (!(Array.isArray(x)) || x.length > 0));
    return arr.length ? arr : undefined;
  }
  if (v && typeof v === 'object') {
    const out = {};
    for (const k of Object.keys(v)) {
      const c = compact(v[k]);
      if (c !== undefined && c !== null && !(Array.isArray(c) && c.length === 0)) out[k] = c;
    }
    return Object.keys(out).length ? out : undefined;
  }
  return v;
};

const blocksForAbout = () => {
  const pageUrl = `${ORIGIN}/about`;
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      '@id': `${pageUrl}#webpage`,
      url: pageUrl,
      name: company.aboutTitle,
      description:
        company.aboutDescription,
      inLanguage: 'en-AU',
      isPartOf: { '@type': 'WebSite', '@id': `${ORIGIN}#website`, url: ORIGIN, name: ENTITY.brand },
      about: { '@id': `${ORIGIN}#organization` },
      mainEntity: { '@id': `${ORIGIN}#organization` },
    },
    {
      '@context': 'https://schema.org',
      ...ORG_NODE,
    },
  ];
};

const blocksForForLenders = () => {
  const pageUrl = `${ORIGIN}/for-lenders`;
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${pageUrl}#webpage`,
      url: pageUrl,
      name: 'For Capital Partners — Curated Private Capital Deal Flow',
      description:
        'Curated deal flow across real estate finance, corporate growth and special situations — through an invitation-only, NDA-gated platform managed by GP Capital.',
      inLanguage: 'en-AU',
      isPartOf: { '@type': 'WebSite', '@id': `${ORIGIN}#website`, url: ORIGIN, name: ENTITY.brand },
      about: { '@id': `${ORIGIN}#organization` },
      provider: { '@id': `${ORIGIN}#organization` },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FinancialService',
      '@id': `${pageUrl}#service`,
      name: 'GP Capital — Lender & Family Office Origination Channel',
      description:
        'Invitation-only deal-flow channel for institutional lenders, private credit funds and family offices. NDA-gated, mandate-matched origination across senior debt, mezzanine, preferred equity and special-situations capital.',
      url: pageUrl,
      provider: ORG_NODE,
      areaServed: ORG_NODE.areaServed,
      audience: {
        '@type': 'BusinessAudience',
        audienceType: 'Institutional lenders, private credit funds, family offices and high-net-worth investment offices',
      },
      serviceType: [
        'Deal origination',
        'Mandate-matched deal flow',
        'Confidential information memoranda (CIM) distribution',
        'NDA-gated data-room access',
        'Term-sheet and LOI submission',
      ],
      offers: {
        '@type': 'Offer',
        name: 'Curated invitation-only deal flow',
        category: 'Institutional capital — private credit / structured finance',
        availability: 'https://schema.org/InStock',
        eligibleCustomerType: 'Business',
      },
      isPartOf: { '@id': `${pageUrl}#webpage` },
    },
  ];
};

const blocksForForBorrowers = () => {
  const pageUrl = `${ORIGIN}/for-borrowers`;
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${pageUrl}#webpage`,
      url: pageUrl,
      name: 'For Borrowers & Sponsors — Access Institutional Private Capital',
      description:
        'Structured access to institutional private capital for real estate finance, business growth, restructuring and special situations — through a controlled, NDA-gated origination process managed by GP Capital.',
      inLanguage: 'en-AU',
      isPartOf: { '@type': 'WebSite', '@id': `${ORIGIN}#website`, url: ORIGIN, name: ENTITY.brand },
      about: { '@id': `${ORIGIN}#organization` },
      provider: { '@id': `${ORIGIN}#organization` },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FinancialService',
      '@id': `${pageUrl}#service`,
      name: 'GP Capital — Borrower & Sponsor Capital Access',
      description:
        'Confidential, NDA-gated origination channel for borrowers and sponsors seeking institutional private capital across senior debt, bridge, construction, mezzanine, preferred equity and special-situations capital.',
      url: pageUrl,
      provider: ORG_NODE,
      areaServed: ORG_NODE.areaServed,
      audience: {
        '@type': 'BusinessAudience',
        audienceType: 'Borrowers, sponsors, developers and operating businesses seeking institutional private capital',
      },
      serviceType: [
        'Deal submission and pre-screening',
        'Confidential information memorandum (CIM) preparation',
        'Counterparty mandate matching',
        'NDA-gated data-room access',
        'Term-sheet and LOI coordination',
        'Capital structure advisory',
      ],
      offers: {
        '@type': 'Offer',
        name: 'Controlled, confidential capital introduction',
        category: 'Private credit / structured finance origination',
        availability: 'https://schema.org/InStock',
        eligibleCustomerType: 'Business',
      },
      isPartOf: { '@id': `${pageUrl}#webpage` },
    },
  ];
};


const blocksForContact = () => {
  const pageUrl = `${ORIGIN}/contact`;
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      '@id': `${pageUrl}#webpage`,
      url: pageUrl,
      name: 'Contact — GP Capital',
      description:
        company.contactDescription,
      inLanguage: 'en-AU',
      isPartOf: { '@type': 'WebSite', '@id': `${ORIGIN}#website`, url: ORIGIN, name: ENTITY.brand },
      about: { '@id': `${ORIGIN}#organization` },
      mainEntity: { '@id': `${ORIGIN}#organization` },
    },
    {
      '@context': 'https://schema.org',
      ...ORG_NODE,
    },
  ];
};

// Phase 71 — /security page. One clean WebPage block linked to the shared
// #organization graph node. Deliberately NO security claims in the schema
// (no certifications, no SLAs, no security-features array). The page itself
// is the source of truth; this just helps crawlers understand the surface.
const blocksForSecurity = () => {
  const pageUrl = `${ORIGIN}/security`;
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${pageUrl}#webpage`,
      url: pageUrl,
      name: 'Security & Responsible Disclosure — GP Capital',
      description:
        'Responsible-disclosure contact and a factual reference to GP Capital\u2019s permissioned platform-access and document-handling model.',
      inLanguage: 'en-AU',
      isPartOf: { '@type': 'WebSite', '@id': `${ORIGIN}#website`, url: ORIGIN, name: ENTITY.brand },
      about: { '@id': `${ORIGIN}#organization` },
      publisher: { '@id': `${ORIGIN}#organization` },
    },
  ];
};

const PAGE_BUILDERS = {
  about: blocksForAbout,
  'for-lenders': blocksForForLenders,
  'for-borrowers': blocksForForBorrowers,
  contact: blocksForContact,
  security: blocksForSecurity,
};

export const SiteJsonLd = ({ page }) => {
  const build = PAGE_BUILDERS[page];
  if (!build) return null;

  const blocks = build()
    .map((b) => compact(b))
    .filter(Boolean);

  return (
    <>
      {blocks.map((node, i) => {
        const t = (node['@type'] || 'unknown').toLowerCase();
        return (
          <script

            key={i}
            type="application/ld+json"
            data-jsonld={`${page}-${t}`}

            dangerouslySetInnerHTML={{ __html: JSON.stringify(node) }}
          />
        );
      })}
    </>
  );
};

export default SiteJsonLd;
